import { Building2, Info, ShieldCheck } from 'lucide-react'

import { INBOX_CANDIDATE_CAP } from '@/api/approvals-api'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { useApprovalPermissions } from './permissions'
import { useCheckerReach } from './queries'
import { describeRequestType } from './request-types'

/**
 * What this checker can be asked to countersign, and the two things about their queue that are
 * true whatever is in it.
 *
 * An empty inbox has two quite different causes — nothing is waiting, or nothing routes to you —
 * and only one of them is worth telling somebody about. So the set of operations is stated up
 * front rather than left to be inferred from a queue that happens to be empty this morning.
 */
export function CheckerCapabilities() {
  const permissions = useApprovalPermissions()
  const reach = useCheckerReach()

  if (permissions.approvableRequestTypes.length === 0) {
    return (
      <Alert tone="info">
        <Info aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Nothing routes to you for countersignature</AlertTitle>
          <AlertDescription>
            Your queue will stay empty: the inbox is defined by the operations you hold the
            countersigning permission for, and your role holds none of them. Those permissions are
            the operation&apos;s own code with <span className="font-mono text-2xs">.approve</span>{' '}
            after it — <span className="font-mono text-2xs">loans.disburse.approve</span> for a
            disbursement. An administrator grants them, and the grant is itself countersigned.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <div className="space-y-2">
      <details className="rounded-card border border-border-subtle bg-surface p-3">
        <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-heading max-lg:min-h-11">
          <ShieldCheck className="size-4 text-brand-text" aria-hidden />
          You may countersign {String(permissions.approvableRequestTypes.length)}{' '}
          {permissions.approvableRequestTypes.length === 1 ? 'operation' : 'operations'}
        </summary>

        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {permissions.approvableRequestTypes.map((requestType) => {
            const descriptor = describeRequestType(requestType)
            return (
              <li key={requestType} className="min-w-0">
                <span className="block truncate text-sm text-body">{descriptor.label}</span>
                <span className="block truncate font-mono text-2xs text-muted">
                  {descriptor.checkerPermission}
                </span>
              </li>
            )
          })}
        </ul>

        <p className="mt-3 text-2xs text-muted">
          A request of one of these types reaches you only if somebody else raised it — the engine
          never shows you your own — and only while it is still within its deadline. Holding the
          permission is not the same as being able to approve any amount: an approval ceiling may
          still apply, and it is not readable from here.
        </p>
      </details>

      {reach.canAccessAllBranches ? (
        <Alert tone="info">
          <Building2 aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Your queue is not limited to your branches</AlertTitle>
            <AlertDescription>
              You may operate across every branch, so the server applies no branch filter to this
              inbox at all — the branches listed against your account are a floor, not the extent of
              it. Expect rows from branches you have never been assigned to, including
              institution-wide requests that belong to no branch.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {!reach.directoryAvailable ? (
        <p className="text-2xs text-muted">
          Branches show as identifiers because no module owns branches as records yet: the API sends
          a null code and name and says so through{' '}
          <span className="font-mono">directoryAvailable</span>. The identifier is the only true
          thing there is to show, and an invented label would be a name the institution never gave
          that branch.
        </p>
      ) : null}

      <p className="text-2xs text-muted">
        The queue is built from the {String(INBOX_CANDIDATE_CAP)} oldest pending requests in your
        reach, and your permissions are applied to them afterwards. If the institution ever has more
        than that waiting at once, the newest are cut before you see them — and nothing in the
        response says so, which is why it is written here rather than shown as a banner that could
        never fire.
      </p>
    </div>
  )
}
